import { API } from '@/lib/api';
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Headphones, Send, Bot, User, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";



const SupportPage = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => `support_${user?.user_id || "anon"}_${Date.now()}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setMessages([{
      role: "assistant",
      content: `Hi ${user?.name?.split(" ")[0] || "there"}! I'm Stack's Support Assistant. How can I help you today? I can assist with bookings, payments, tracking, account issues, and more.`,
      timestamp: new Date().toISOString(),
    }]);
    inputRef.current?.focus();
  }, [user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;

    setMessages((prev) => [...prev, { role: "user", content: text, timestamp: new Date().toISOString() }]);
    setInput("");
    setSending(true);

    try {
      const res = await axios.post(`${API}/support-chat`, { message: text, session_id: sessionId }, { withCredentials: true });
      setMessages((prev) => [...prev, { role: "assistant", content: res.data.reply, timestamp: new Date().toISOString() }]);
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", content: "Sorry, I'm having trouble connecting. Please try again.", timestamp: new Date().toISOString() }]);
    }
    setSending(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const clearChat = () => {
    setMessages([{ role: "assistant", content: "Chat cleared. How can I help you?", timestamp: new Date().toISOString() }]);
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col" data-testid="support-page">
      <Navbar />
      <div className="pt-20 flex-1 flex flex-col max-w-3xl mx-auto w-full">
        {/* Header */}
        <div className="px-4 py-4 border-b border-stone-200 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/client/settings" className="text-stone-500 hover:text-emerald-950 transition-colors" data-testid="back-link">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                <Headphones className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h1 className="font-bold text-emerald-950 text-base" data-testid="support-title">Help & Support</h1>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-green-500 rounded-full" />
                  <span className="text-xs text-stone-500">AI Assistant - Always Online</span>
                </div>
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={clearChat} className="text-stone-400 hover:text-red-500" data-testid="clear-chat-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="messages-container">
          {messages.map((msg, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className={`flex gap-2.5 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              data-testid={`message-${idx}`}
            >
              {msg.role === "assistant" && (
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center shrink-0 mt-1">
                  <Bot className="w-4 h-4 text-emerald-600" />
                </div>
              )}
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                msg.role === "user"
                  ? "bg-emerald-600 text-white rounded-br-md"
                  : "bg-white border border-stone-200 text-stone-700 rounded-bl-md"
              }`}>
                {msg.content}
              </div>
              {msg.role === "user" && (
                <div className="w-8 h-8 bg-emerald-950 rounded-full flex items-center justify-center shrink-0 mt-1">
                  <User className="w-4 h-4 text-white" />
                </div>
              )}
            </motion.div>
          ))}
          {sending && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-2.5">
              <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center shrink-0">
                <Bot className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="bg-white border border-stone-200 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </motion.div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Suggestions */}
        {messages.length <= 1 && (
          <div className="px-4 pb-2">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" data-testid="quick-suggestions">
              {["How do I track my booking?", "Can I cancel my order?", "How do refunds work?", "What services are available?", "How do I change payment method?"].map((q) => (
                <button
                  key={q}
                  className="shrink-0 text-xs bg-white border border-stone-200 rounded-full px-3 py-1.5 text-stone-600 hover:border-emerald-300 hover:bg-emerald-50 transition-colors"
                  onClick={() => { setInput(q); inputRef.current?.focus(); }}
                  data-testid={`suggestion-${q.slice(0, 10)}`}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="px-4 py-3 bg-white border-t border-stone-200 pb-20 sm:pb-3" data-testid="input-area">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your question..."
              className="flex-1 bg-stone-50 border border-stone-200 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 transition-colors"
              disabled={sending}
              data-testid="chat-input"
            />
            <Button
              size="icon"
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full w-10 h-10 shrink-0"
              onClick={handleSend}
              disabled={!input.trim() || sending}
              data-testid="send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
